import { useState, type FormEvent } from 'react';
import { Check, ChevronDown, Plus, Settings } from 'lucide-react';

export type ProjectOption = { id: string; name: string; slug: string; role: string; accent_color: string };

type UpdateProject = (projectId: string, values: { name: string; slug: string }) => Promise<string | null>;

export function ProjectSwitcher({ projects, value, onChange, onCreate, onUpdate }: {
  projects: ProjectOption[];
  value: string;
  onChange: (projectId: string) => void;
  onCreate: (name: string) => Promise<string | null>;
  onUpdate: UpdateProject;
}) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ProjectOption | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const selected = projects.find((project) => project.id === value) ?? projects[0];

  const submitCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    const result = await onCreate(name);
    setBusy(false);
    if (result) return setError(result);
    setName(''); setCreating(false);
  };

  const openSettings = (project: ProjectOption) => {
    setOpen(false); setEditing(project); setName(project.name); setSlug(project.slug); setError('');
  };

  const submitSettings = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing || busy) return;
    setBusy(true); setError('');
    const result = await onUpdate(editing.id, { name, slug });
    setBusy(false);
    if (result) return setError(result);
    setEditing(null);
  };

  return <>
    <div className="project-switcher" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false); }} onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}>
      <button type="button" className="project-trigger" aria-label="Активный проект" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
        <span className="project-identity"><strong>{selected?.name}</strong></span>
        <ChevronDown size={15} aria-hidden="true" />
      </button>
      {open && <div className="project-popover" role="dialog" aria-label="Выбор проекта">
        <div className="project-popover-head"><span>Проекты</span><small>{projects.length}</small></div>
        <div className="project-listbox" role="listbox" aria-label="Проекты">
          {projects.map((project) => <div key={project.id} className={project.id === selected?.id ? 'project-option-row selected' : 'project-option-row'}>
            <button type="button" role="option" aria-selected={project.id === selected?.id} className="project-option" onClick={() => { onChange(project.id); setOpen(false); }}>
              <strong>{project.name}</strong>
              <Check className="project-check" size={15} aria-hidden="true" />
            </button>
            {(project.role === 'owner' || project.role === 'admin') && <button type="button" className="project-settings-button" aria-label={`Настройки проекта ${project.name}`} title="Настройки проекта" onClick={() => openSettings(project)}><Settings size={15} /></button>}
          </div>)}
        </div>
        <div className="project-menu-actions"><button type="button" onClick={() => { setOpen(false); setCreating(true); setName(''); setError(''); }}><Plus size={15}/>Создать проект</button></div>
      </div>}
    </div>
    {creating && <div className="project-modal-overlay" onMouseDown={() => setCreating(false)}><section className="project-modal" role="dialog" aria-modal="true" aria-labelledby="project-create-title" onMouseDown={(event) => event.stopPropagation()}><h2 id="project-create-title">Новый проект</h2><form onSubmit={submitCreate}><label><span>Название</span><input autoFocus value={name} onChange={(event) => setName(event.target.value)} maxLength={80} placeholder="Название проекта"/></label>{error && <p>{error}</p>}<div><button type="button" className="ghost-button" onClick={() => setCreating(false)}>Отмена</button><button type="submit" className="primary-button" disabled={busy || name.trim().length < 2}>{busy ? 'Создаём…' : 'Создать'}</button></div></form></section></div>}
    {editing && <div className="project-modal-overlay" onMouseDown={() => setEditing(null)}><section className="project-modal" role="dialog" aria-modal="true" aria-labelledby="project-settings-title" onMouseDown={(event) => event.stopPropagation()}><h2 id="project-settings-title">Настройки проекта</h2><form onSubmit={submitSettings}><label><span>Название</span><input autoFocus value={name} onChange={(event) => setName(event.target.value)} maxLength={80}/></label><label><span>Код проекта</span><input value={slug} onChange={(event) => setSlug(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-{2,}/g, '-'))} maxLength={63} pattern="[a-z0-9]+(?:-[a-z0-9]+)*"/></label><div className="project-modal-meta"><span>Роль</span><strong>{editing.role === 'owner' ? 'Владелец' : 'Администратор'}</strong></div>{error && <p>{error}</p>}<div><button type="button" className="ghost-button" onClick={() => setEditing(null)}>Отмена</button><button type="submit" className="primary-button" disabled={busy || name.trim().length < 2 || slug.length < 2}>{busy ? 'Сохраняем…' : 'Сохранить'}</button></div></form></section></div>}
  </>;
}
